/**
 * B76B lane B — `useAccountRole`: the ONE authoritative role fact.
 *
 * `GET /api/user/role` → `{ role: 'athlete' | 'coach' | 'athlete_coach' }`
 * (lane A). This replaces the signal composition the client has been doing
 * since CG-7/UAT-B4 — `_account_role` + `_onboarding_completed*` + "trains ≥1
 * team" — which every surface re-derived slightly differently. Handoff §State
 * Management: "single server-authoritative fact, replacing the
 * `_account_role`/`_onboarding_completed`/`isCoach` signal composition. Every
 * surface fork reads it."
 *
 * ## Caching
 *
 * Module-level per-user-id cache + in-flight dedupe, the `useGuardianMode` /
 * `useCoachMode` / `useAdminGate` pattern. This hook mounts in nav chrome and
 * on every athlete surface (the 1c guard), so an uncached fetch per mount
 * would hammer the endpoint. Same staleness trade-off as its siblings: a role
 * that changes mid-session (a first team created, a membership bought) lands
 * on the next full page load. The two flows that CHANGE the role both end in a
 * full remount of the app shell, so this is not a live-drift risk in practice;
 * `__resetAccountRoleCache()` is the escape hatch if a lane ever needs one.
 *
 * ## ⚖️ THE FAIL DIRECTION IS NOT SYMMETRIC
 *
 * The handoff's hard rule (§Interactions): "hold skeletons while the role fact
 * loads — never flash athlete UI at a coach or vice versa." So this hook
 * deliberately exposes TWO different answers, and callers must pick the right
 * one:
 *
 *   · `role` — fail-open `'athlete'`. SAFE ONLY for athlete/athlete_coach
 *     -facing decisions, where being wrong costs a reader an affordance they
 *     could not use anyway (the B53/B70 fail-open discipline).
 *
 *   · `coachOnly` — the COACH-BOUNDARY answer. Never a naive athlete default:
 *     when the server fact has not resolved it degrades to the PRE-MIGRATION
 *     composed signal (`useCoachMode().coachOnly`), which already identifies a
 *     declared coach from athlete context that is loaded by then. Callers hold
 *     the skeleton while `boundaryPending` and never render athlete UI under it.
 *
 * A naive `'athlete'` default on the boundary path would show a coach the
 * athlete app for a frame on every slow network — exactly the flash the rule
 * forbids. Degrading to the legacy signal instead means the WORST case of a
 * failed fetch is today's production behaviour, never a regression.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';
import { useCoachMode } from './useCoachMode';

export type AccountRole = 'athlete' | 'coach' | 'athlete_coach';

const ROLES: readonly AccountRole[] = ['athlete', 'coach', 'athlete_coach'];

/** The fail-open default. Athlete-facing decisions only — see the header. */
const FAIL_OPEN_ROLE: AccountRole = 'athlete';

/**
 * ⚖️ B47 fix 8 (round-36 critic F1) — THE ROLE FETCH HAS AN END TOO.
 *
 * `AthleteSurfaceGuard` holds every guarded route on `boundaryPending`, which
 * is `loading || contextLoading`. B47 fix 6 bounded the context read at 10 s
 * and left this term unbounded, so the hold it was built to end simply
 * continued on the other one: the request goes through `useAuthFetch` →
 * `authFetch.ts`, a bare `fetch` with no signal and no timeout, and its
 * precondition refreshes the token against the same host. On a half-open
 * socket — a captive portal, a shell relaunched on wifi that accepts and never
 * answers — `status` stayed `'pending'` for as long as the platform allowed
 * (60 s on WKWebView) with nothing on screen but an empty busy element.
 *
 * The same 10 s as the read, for the same reason (`useAthleteContext.tsx`),
 * and the same shape: the fetch is ABANDONED, not aborted. The request is not
 * cancelled and its answer is downloaded and dropped, because a real
 * `AbortSignal` would have to reach every fetch double in the repo — the
 * substitution the coordinator ratified for fix 6, applied again here.
 *
 * On the deadline the fact settles `failed`, which is the fail direction this
 * hook's header documents: `role` fails open to athlete, and `coachOnly`
 * degrades to the pre-migration signal rather than guessing.
 *
 * ⚖️ B47 fix 9 (round-46 critic S2 and S6) — WHAT THE DEADLINE IS NOT.
 *
 * It is not an answer, and it is not the end of the request. Two corrections to
 * what this block used to claim:
 *
 *   · A SLOW FETCH IS NOT A FAILED FETCH. The abandoned request is still
 *     listened to: if it answers while this instance is mounted and
 *     uncancelled, it settles the instance on its real answer. Without that,
 *     the late 200 wrote the module cache and nothing nudged the mounted
 *     instances — the guard that mounted at boot stayed `failed` (so `coachOnly`
 *     was false, and a coach whose row carries no `_account_role` got the
 *     ATHLETE surface) while anything mounting a second later read `coach` from
 *     the cache. Two instances of "the ONE authoritative role fact",
 *     permanently disagreeing. The rule is the context read's: a read that is
 *     merely slow still lands.
 *
 *   · `refetch()` HEALS IT NOW, AND DID NOT BEFORE. The press dropped the cache
 *     entry and left `inflight` alone, so the re-run effect re-attached to the
 *     dead promise: three presses, one request, `failed` re-reported at every
 *     deadline. A press SUPERSEDES — it drops the in-flight entry and advances
 *     that account's generation, so the next effect run issues a real request
 *     and the superseded one can no longer write the cache under it.
 *
 * ⛔ The third term of the hold is NOT bounded here: the auth boot's own
 * `getSession()` (`contexts/AuthContext.tsx`) has no deadline, and while it
 * runs `userId` is null, no read starts, and no deadline is armed anywhere.
 * That is B147, filed, and it is not this file.
 */
const ROLE_DEADLINE_MS = 10_000;

export interface AccountRoleState {
  /**
   * The role fact is still in flight. Coach-boundary surfaces hold their
   * skeleton on `boundaryPending` (which includes this); athlete-facing
   * callers may read `role` as soon as this is false.
   */
  loading: boolean;
  /**
   * The role. Server truth once `resolved`; the fail-open `'athlete'`
   * otherwise. ⚠️ Athlete/athlete_coach-facing decisions ONLY — a coach
   * boundary must read `coachOnly`, never this.
   */
  role: AccountRole;
  /** The server answered authoritatively. */
  resolved: boolean;
  /** Settled without an authoritative answer (network, 404 before lane A
   *  lands, signed out). `role` is the fail-open default and `coachOnly`
   *  is the pre-migration signal. */
  failed: boolean;
  /**
   * THE COACH-BOUNDARY ANSWER. True for a coach-first account with no athlete
   * side. Server truth when resolved; the pre-migration composed signal when
   * not. Safe to render a boundary on; never flashes athlete UI at a coach.
   */
  coachOnly: boolean;
  /** Hold the skeleton while true. Covers auth, athlete context and the
   *  role fetch — the `useRoleGate` discipline, one field. */
  boundaryPending: boolean;
  /** `athlete` | `athlete_coach` — this account has a personal training side. */
  hasAthleteSide: boolean;
  /** `coach` | `athlete_coach` — this account has a coaching side. */
  hasCoachSide: boolean;
  /**
   * ⚖️ B47 fix 7 — ASK THE SERVER AGAIN. A failed role fetch used to be final
   * for the life of the mount (the effect's deps never change again), so an
   * athlete whose whole backend blinked stayed on the degraded path until she
   * reloaded the app. Today's "Try again" calls this beside
   * `useAthleteContext.reload()`, so one press heals both reads.
   *
   * It does NOT return the fact to `'pending'`: the previous answer stands
   * until a new one lands, which is what keeps this from re-holding every
   * surface that watches `boundaryPending`. No polling, no retry loop — one
   * request per press.
   *
   * ⚖️ B47 fix 9 — and one REAL request. A press supersedes whatever is still
   * out (the in-flight entry is dropped and the account's generation advances),
   * so a request that is merely slow, or one the deadline has already given up
   * on, cannot stand in for the press's own.
   */
  refetch: () => void;
  /**
   * ⚖️ B182 (decision Y, 2026-09-23) — true only for the AMBIGUOUS hold: a
   * failed role fetch over a bare row this account has in hand (see
   * `holdOnAmbiguousFailure` in the hook body). Distinct from
   * `boundaryPending`'s general "still deciding" — auth loading, context
   * loading, or an ordinary in-flight fetch all set that too. The boundary
   * reads this one field to know when to paint its quiet retry control,
   * never on the ordinary skeleton.
   */
  ambiguousHold: boolean;
}

/** The settled outcomes of the role fetch. `failed` is a real outcome, not an
 *  absence: it is what routes callers to their documented fallback. */
type Settled =
  | { status: 'resolved'; role: AccountRole }
  | { status: 'failed' };

type RoleState = { status: 'pending' } | Settled;

const cache = new Map<string, Settled>();
const inflight = new Map<string, Promise<Settled>>();
/**
 * ⚖️ B47 fix 9 (round-44 critic F4, round-46 critic S6) — THE PRESS TOKEN.
 *
 * One counter per account, advanced by `refetch()`. A request captures the
 * generation it was issued under and writes the module cache only if that
 * generation is still current, so an answer that arrives after a press cannot
 * re-cache a fact the press just dropped. The instance side of the same
 * supersede is the effect's own `cancelled` flag, which the re-run sets.
 *
 * ⚖️ AND A FAILED ANSWER IS NOT CACHED (F3b, decided here). The cache holds
 * facts about accounts; a network failure is a fact about one attempt, and
 * caching it would make a blip permanent for the session with only Today's
 * "Try again" — unreachable from `/plan` and `/run` — able to clear it. So a
 * throw, a non-ok status and an abandoned request all leave the cache empty and
 * the next mount asks again. The cost is the one round-46 critic S6 measured:
 * on a host that is not answering, a guarded-route mount holds for its own 10 s
 * before degrading. The one failure that IS cached is the 200 carrying a role
 * string this client does not recognise — the server answered, and asking again
 * gets the same answer.
 *
 * ⚖️ B47 fix 10 (round-52 critic F2) — AND THAT SENTENCE IS TRUE OF AN
 * ABANDONED REQUEST ONLY SINCE THIS FIX. The cache was indeed empty, but the
 * dead promise stayed in `inflight` (it is deleted in a `finally` that never
 * runs), so the next mount did not ask: it waited on the request the deadline
 * had already given up on, for its own 10 s, for ever. The deadline drops that
 * entry now — a second socket against a host that is dead but still accepting,
 * per mount, deliberately paid so that a network coming back heals with no
 * press.
 *
 * ⚖️ B47 fix 11 (round-56 critic F1) — AND THE DEADLINE DOES NOT TOUCH THIS
 * COUNTER. It advances on a press and on nothing else: an answer slower than
 * its own deadline is a slow answer, not a superseded one, so it still writes
 * the cache and the next mount reads the fact with no request at all.
 *
 * ⚖️ B47 fix 12 (round-58 critic F1, B162/B173) — AND IT DOES NOT ORDER TWO
 * REQUESTS AT THE SAME GENERATION AGAINST EACH OTHER. The deadline drop (fix
 * 10) lets a second real request go out for an account whose first is still
 * unanswered, and fix 11 kept both of them able to write — so when neither is
 * superseded by a press, this counter alone cannot say which answer is the
 * one to keep: it stamps every request issued after the drop with the SAME
 * generation. Landing order used to decide instead, which let the
 * OLDER-issued request's answer overwrite the newer one just by taking longer
 * — see `issueSeq`/`lastCached` below, which fixes that.
 */
const generation = new Map<string, number>();
/**
 * ⚖️ B47 fix 12 (round-58 critic F1, B162/B173) — ISSUE ORDER, NOT LANDING
 * ORDER. `issueSeq` is a per-uid counter stamped once, synchronously, the
 * moment a REAL request goes out (never on a reused in-flight promise); the
 * stamp — not the clock — is this request's place in line. `lastCached`
 * records the stamp the cache currently holds for that uid. A request may
 * write only when its own stamp is at least that value: `mySeq >=
 * lastCached.get(uid)`. Two requests at the same generation (the deadline
 * drop's doing, not a press) are still each listened to per fix 9, but the
 * one issued later always wins the cache, whichever socket answers first —
 * request 2 landing at 11.5 s and request 1 landing at 12 s both write
 * `lastCached`-gated, so request 1's later, older-issued answer is refused
 * and request 2's stands. `refetch()` deletes this account's entry along with
 * the cache and the in-flight one, so a press's own request starts a fresh
 * count rather than being held to a stamp a dropped answer left behind.
 */
const lastCached = new Map<string, number>();
const issueSeq = new Map<string, number>();
/**
 * ⚖️ B47 fix 7 — every mounted instance, so `refetch()` reaches all of them.
 *
 * The cache is module-level but the STATE is per instance, and `/today` mounts
 * this hook at least twice: `AthleteSurfaceGuard` wraps the route, and the
 * retry surface inside it is what presses. Dropping the cache entry alone
 * would leave the guard's own instance on its stale `failed` for ever, because
 * nothing would re-run its effect. So a refetch drops the entry and then
 * nudges every instance, which re-runs one fetch (the in-flight map dedupes
 * the rest) and settles them all on the same answer.
 */
const listeners = new Set<() => void>();

/**
 * ⚖️ B182 (decision Y, 2026-09-23) — THE HOLD RE-ASKS ON ITS OWN, BOUNDED.
 *
 * `holdOnAmbiguousFailure` below (declared in the hook body) keeps
 * `boundaryPending` true rather than guess a coach from an ambiguous row —
 * the right call, but the 2026-09-23 harvest's critic (finding 2) and
 * security (finding 5) reviews read the hold that followed it: nothing
 * re-asked, and the only way out was a reload. Decision Y is explicit: re-ask
 * on a bounded backoff, and leave a quiet manual control for whenever the
 * bound is spent (`AthleteSurfaceBoundary.tsx` paints it).
 *
 * Armed at MODULE level, once per account, not per mounted instance: this
 * hook mounts several times at once for one reader (nav chrome, the guard,
 * `useCanCreateTeam`), and a per-instance timer would turn one bounded
 * backoff into one per mount — exactly the multiplication fix 8's own
 * deadline exists to avoid on the request itself.
 *
 * ⚠️ BOTH ATTEMPTS ARE SCHEDULED UP FRONT, AT THE MOMENT THE HOLD BEGINS —
 * not one, waiting to see if it failed, before scheduling the next. `settle`
 * (the hook body, below) deliberately bails out of a state update when a
 * fresh answer is `failed` and the previous one already was: "a no-op state
 * write when nothing changed", the guard against a re-render loop on a host
 * that keeps refusing. That means a SECOND consecutive failure produces no
 * re-render at all, so an effect watching this hook's own output cannot be
 * the thing that arms the next attempt — it would never fire again after the
 * first. Two fixed, cumulative delays from hold-onset — 5 s, then 15 s more
 * (20 s total) — sidestep that entirely: both timers exist from the start,
 * and only an unambiguous resolve cancels the pair (the effect below —
 * narrower than "the hold ended", and deliberately so; see its own comment).
 * A manual press (`refetch`) does not touch this schedule either way: if it
 * resolves things, the effect's own transition clears the rest; if it fails
 * again, the background schedule it did not disturb is still healing on its
 * own timeline.
 */
const AMBIGUOUS_HOLD_BACKOFF_MS = [5_000, 15_000] as const;
const ambiguousHoldTimers = new Map<string, ReturnType<typeof setTimeout>[]>();

function clearAmbiguousHoldBackoff(uid: string): void {
  const timers = ambiguousHoldTimers.get(uid);
  if (timers) for (const timer of timers) clearTimeout(timer);
  ambiguousHoldTimers.delete(uid);
}

/**
 * The plain-function core of a press: drop the settled answer and the
 * in-flight request for this account and advance its generation, so the next
 * mounted effect issues a real request instead of reading a stale cache or
 * waiting on a request a press or a deadline already gave up on. `refetch()`
 * below is this function; the backoff timers call it directly because a
 * timer is not a mounted instance and has no `uid` closure of its own to read
 * a hook's `refetch` from.
 */
function forceReask(uid: string): void {
  cache.delete(uid);
  inflight.delete(uid);
  generation.set(uid, (generation.get(uid) ?? 0) + 1);
  lastCached.delete(uid);
  for (const bump of listeners) bump();
}

function armAmbiguousHoldBackoff(uid: string): void {
  if (ambiguousHoldTimers.has(uid)) return; // already armed for this hold
  let elapsed = 0;
  const timers = AMBIGUOUS_HOLD_BACKOFF_MS.map((delay) => {
    elapsed += delay;
    return setTimeout(() => forceReask(uid), elapsed);
  });
  ambiguousHoldTimers.set(uid, timers);
}

/** Test-only — clear the module-level role cache. */
export function __resetAccountRoleCache(): void {
  cache.clear();
  inflight.clear();
  generation.clear();
  lastCached.clear();
  issueSeq.clear();
  for (const timers of ambiguousHoldTimers.values()) {
    for (const timer of timers) clearTimeout(timer);
  }
  ambiguousHoldTimers.clear();
}

function parseRole(body: unknown): AccountRole | null {
  if (!body || typeof body !== 'object') return null;
  const value = (body as { role?: unknown }).role;
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
    ? (value as AccountRole)
    : null;
}

export function useAccountRole(): AccountRoleState {
  const { loading: authLoading, session, user } = useAuthContext();
  const { loading: contextLoading, raw: athleteRaw, rawIsMine } = useAthleteContext();
  // The pre-migration fallback. Read unconditionally (hooks order is fixed);
  // consulted only when the server fact did not resolve.
  const { coachOnly: legacyCoachOnly } = useCoachMode();
  const fetchAuthed = useAuthFetch();
  const [state, setState] = useState<RoleState>({ status: 'pending' });
  /** Bumped by `refetch()` on every mounted instance — the fetch effect's one
   *  re-run trigger that is not an identity change (B47 fix 7). */
  const [refetchTick, setRefetchTick] = useState(0);

  // Depend on stable primitives, not object identities — this hook mounts in
  // nav chrome, where identity churn would loop (the useGuardianMode note).
  const uid = user?.id ?? null;
  const hasSession = !!session;

  // ⚖️ B47 fix 7 — subscribe to the module-level nudge (see `listeners`).
  useEffect(() => {
    const bump = () => setRefetchTick((n) => n + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  const refetch = useCallback(() => {
    // Drop the settled answer for THIS account only — another account's entry
    // is still good.
    //
    // ⚖️ B47 fix 9 (round-44 critic F4, round-46 critic S6) — AND THE REQUEST
    // ALREADY OUT IS SUPERSEDED. Leaving `inflight` alone meant the re-run
    // effect found the dead promise and re-attached to it: on a host that
    // accepts and never answers, three presses issued ONE request and each
    // press waited out its own deadline to be told `failed` again. A press is
    // the athlete saying "ask again", so it asks again: the entry goes, the
    // generation advances, and the superseded request can no longer put its
    // answer in the cache under the press's own request.
    if (uid) {
      // ⚖️ B47 fix 12 — the press's own request starts a fresh issue-order
      // count rather than being held to a stamp a dropped answer left behind
      // (folded into `forceReask`, below).
      //
      // ⚖️ B182 (decision Y) — deliberately does NOT touch the ambiguous-hold
      // backoff schedule (`ambiguousHoldTimers`). If this press resolves the
      // account, the hold-ends effect below clears it on the genuine
      // transition; if the press fails again, the background schedule it
      // never disturbed is still healing on its own timeline — a press adds
      // a door, it does not reset the other one.
      forceReask(uid);
      return;
    }
    for (const bump of listeners) bump();
  }, [uid]);

  /**
   * ⚖️ B47 fix 6 (B131) — THE ANSWER BELONGS TO THE ACCOUNT IT WAS ASKED ABOUT.
   *
   * `state` used to survive a `uid` change: on an in-place account switch (a
   * cross-tab `SIGNED_IN`, or `setSession` from a deep link —
   * `lib/nativeAuth.ts:117`) this hook went on saying `resolved` about the
   * PREVIOUS account for the whole of the new account's role fetch.
   * `AthleteSurfaceGuard` holds on `boundaryPending`, which is
   * `loading || contextLoading`, so the hold survived only as long as the new
   * account's CONTEXT read did. In the other ordering — the context read comes
   * back first, which is the everyday one on a warm row — nothing was pending
   * any more and the guard forked on the old account's role, rendering athlete
   * Today at an account the server answers 403 for. Returning to `'pending'`
   * gives the guard something to hold on in that ordering too.
   *
   * The `useChatHistory.ts:332` pattern: an effect on the id alone. It is
   * DECLARED BEFORE the fetch effect below, so on a uid change with a cached
   * answer both land in one flush and the cache still wins; the functional
   * update bails out at mount, where the state is already pending.
   *
   * ⚠️ What this cannot reach is the single commit in which `uid` has already
   * changed and no effect has run yet — a render-phase problem, and the reason
   * the guard also latches the id it settled for (B131 keeps the rest).
   */
  useEffect(() => {
    setState((prev) => (prev.status === 'pending' ? prev : { status: 'pending' }));
  }, [uid]);

  useEffect(() => {
    // No-op state writes when nothing changed — this hook mounts in nav
    // chrome, so an identity-churn re-render loop would hang the app.
    const settle = (s: Settled) =>
      setState((prev) => {
        if (prev.status === s.status) {
          if (s.status === 'failed') return prev;
          if (prev.status === 'resolved' && prev.role === s.role) return prev;
        }
        return s;
      });

    if (authLoading) return;
    // Signed out — no role fact exists. Settle as failed (not resolved) so the
    // boundary path degrades to the legacy signal rather than asserting
    // "athlete" about an account we know nothing about. Signed-out readers hit
    // the routes' own redirects long before any role fork.
    if (!hasSession || !uid) {
      settle({ status: 'failed' });
      return;
    }

    let cancelled = false;

    const cached = cache.get(uid);
    if (cached) {
      settle(cached);
      return;
    }

    // ⚖️ B47 fix 9 — the generation this request belongs to. A press advances
    // it, and an answer from an older generation may not write the cache.
    const issuedAt = generation.get(uid) ?? 0;
    const fetchRole =
      inflight.get(uid) ??
      (async (): Promise<Settled> => {
        // ⚖️ B47 fix 12 — stamped synchronously, before the first await, so it
        // is this request's place in line the instant it really goes out.
        const mySeq = (issueSeq.get(uid) ?? 0) + 1;
        issueSeq.set(uid, mySeq);
        const res = await fetchAuthed('/api/user/role');
        if (!res.ok) throw new Error(`role check failed (${res.status})`);
        const role = parseRole(await res.json());
        // An unrecognised role value is NOT an authoritative answer about a
        // shape this client understands — degrade rather than guess.
        const result: Settled = role ? { status: 'resolved', role } : { status: 'failed' };
        // ⚖️ B47 fix 12 — issue order, not landing order: this answer may
        // write only if no press has superseded it (generation, fix 9) AND no
        // later-issued request already wrote a fresher answer (mySeq).
        if ((generation.get(uid) ?? 0) === issuedAt && mySeq >= (lastCached.get(uid) ?? 0)) {
          cache.set(uid, result);
          lastCached.set(uid, mySeq);
        }
        return result;
      })();
    inflight.set(uid, fetchRole);

    // ⚖️ B47 fix 8 — the deadline. Armed only when a request is really going
    // out: the cached and signed-out paths above return before this line, so a
    // hook that answers from cache leaves no timer behind, exactly as it did.
    //
    // ⚖️ B47 fix 9 — it is no longer RACED against the request (there is no
    // token to tell them apart any more): the two are separate subscriptions,
    // because both of them have something to say. The deadline says "stop
    // holding"; the request, whenever it lands, says what the answer is.
    let deadline: ReturnType<typeof setTimeout> | undefined;
    const deadlinePassed = new Promise<void>((resolve) => {
      deadline = setTimeout(resolve, ROLE_DEADLINE_MS);
    });

    // The REQUEST's own life is unchanged by the deadline: it is still listened
    // to, and whenever it really settles it clears its entry if that entry is
    // still its own.
    //
    // ⚖️ B47 fix 10 (round-52 critic F2) — WHAT THE DEADLINE DOES END IS ITS
    // TURN AS THE ANSWER EVERY LATER MOUNT WAITS ON (see the drop below). Fix 9
    // reasoned that clearing `inflight` at the deadline "would let the next
    // mount start a second request while the first is still out, which is the
    // opposite of what the map is for". That reasoning is given up here, on
    // purpose: `finally` never runs on a promise that never settles, so on the
    // half-open socket this deadline exists for the entry was never cleared at
    // all and `inflight.get(uid) ??` handed the dead promise to every mount
    // after it — three mounts, ONE request, each paying its own 10 s and then
    // degrading.
    //
    // ⚖️ B47 fix 9 — and it clears ITS OWN entry. A press replaces the entry
    // (see `refetch`), so a superseded request finishing later must not delete
    // the live one and send the mount after it to the network again.
    //
    // ⚖️ B47 fix 9 (round-46 critic S2) — THE LATE ANSWER STILL SPEAKS.
    // Whichever way the request ends, if this instance is still here it settles
    // on it. After a deadline that means `failed` gives way to the real answer
    // when it lands, so no two mounted instances can hold different facts about
    // one account.
    void fetchRole
      .then(
        (s) => {
          if (!cancelled) settle(s);
        },
        () => {
          if (!cancelled) settle({ status: 'failed' });
        },
      )
      .finally(() => {
        if (inflight.get(uid) === fetchRole) inflight.delete(uid);
        // Nothing is waiting on the timer once the request has ended.
        if (deadline !== undefined) clearTimeout(deadline);
      });

    void deadlinePassed.then(() => {
      // An abandoned fetch is a FAILED fetch, never a guess about an account
      // nobody answered for — and never the end of the request above.
      if (!cancelled) settle({ status: 'failed' });
      /**
       * ⚖️ B47 fix 10 (round-52 critic F2) — AND THE ABANDONED REQUEST STOPS
       * ANSWERING FOR THE NEXT MOUNT.
       *
       * The entry goes, so the next mount issues a REAL request instead of
       * waiting on the one this client has already given up on.
       *
       * ⚖️ B47 fix 11 (round-56 critic F1) — AND THAT IS ALL THE DROP DOES:
       * the account's generation does NOT advance here, so an answer that
       * lands after its own deadline still settles the instances listening to
       * it AND still writes the module cache. Fix 10 advanced it, which
       * refused that write: on a network slower than the deadline the fact was
       * then never cached, and every guarded-route mount paid a fresh 10 s
       * hold and re-opened the fail-open window at a coach whose row carries no
       * `_account_role`. The request that replaced this one asks the same
       * endpoint about the same account, but a later socket can still answer
       * FIRST — that is exactly what happened here, this deadline dropping the
       * entry so a fresh request could go out while the abandoned one is still
       * unanswered. ⚖️ B47 fix 12 (round-58 critic F1) struck the sentence that
       * used to stand here — "whichever lands second writes the same value" —
       * because it is not true: two requests at the same generation can answer
       * with DIFFERENT values (a role backfill landing between them, or a slow
       * socket now racing a fast one issued after it dropped), and landing
       * order used to decide, so the older-issued request winning the race
       * could overwrite the newer one's already-cached answer. The true rule
       * is issue order: `lastCached`/`issueSeq` (declared by `generation`
       * above) let a request write only if no later-issued request already
       * has, whichever socket answers first. Only a PRESS supersedes on
       * `generation`, and `refetch()` keeps its own advance.
       *
       * ⚠️ THE PRICE, ACCEPTED. A host that is dead but still accepting gets a
       * second socket per mount instead of one for the session. That is what
       * "the next mount asks again" costs, and it is the cheaper half of the
       * trade: the alternative — the one shipped in fix 9 — is a coach on the
       * athlete surface until the app is relaunched, with no press anywhere
       * able to reach him. Only ITS OWN entry, so a press that has already
       * superseded this request keeps the one it issued.
       */
      if (inflight.get(uid) === fetchRole) inflight.delete(uid);
    });

    return () => {
      cancelled = true;
      if (deadline !== undefined) clearTimeout(deadline);
    };
  }, [authLoading, hasSession, uid, fetchAuthed, refetchTick]);

  const loading = authLoading || state.status === 'pending';
  const resolved = !loading && state.status === 'resolved';
  const failed = !loading && state.status === 'failed';
  const role: AccountRole =
    state.status === 'resolved' && !authLoading ? state.role : FAIL_OPEN_ROLE;

  /**
   * ⚖️ B182 (decision Y, 2026-09-23) — A FAILED FETCH OVER A ROW WITH NO
   * ROLE MARKER HOLDS; IT NEVER SETTLES INTO THE ATHLETE SURFACE.
   *
   * On the degraded path (`failed`) the boundary answer below is
   * `legacyCoachOnly` — `raw._account_role === 'coach' && !athleteOnboarded`
   * (`useCoachMode.ts`). That predicate is trustworthy whenever the row
   * carries EITHER signal: `_account_role` (however it reads — the choosing-
   * athlete fork never writes one at all, only guardian/coach do, so its mere
   * presence already says something), or `_onboarding_completed_at` (a
   * completed athlete, definitionally not coach-only regardless of
   * `_account_role`). What it cannot answer for is a BARE row — neither
   * written — the exact shape `useCoachMode.ts`'s own header calls "a row the
   * legacy signal knows nothing about" and the one the migration-window veto
   * cannot rescue either. Guessing "not a coach" there is the leak this guard
   * exists to close: a coach provisioned before either write lands, on a
   * network blip, would land on the athlete surface instead of the boundary.
   *
   * So this account's own row is inspected directly. If the role fetch
   * failed AND the row this hook can attribute to THIS account (settled,
   * `rawIsMine` — a row we actually have in hand) is BARE, the boundary fact
   * stays pending instead of settling on the legacy guess: `boundaryPending`
   * never releases for that account, so `AthleteSurfaceGuard` keeps holding
   * its skeleton (the pre-fix-5 behaviour, reused for this one case) rather
   * than rendering `children`. This adds no new wait: reaching `failed`
   * already cost the role fetch's own 10 s deadline (fix 8) — this only
   * declines to end an already-bounded hold on a guess.
   *
   * A row carrying either signal is unchanged: `legacyCoachOnly` already
   * answers correctly for it, and this clause never fires.
   *
   * ⚠️ NOT when the CONTEXT read itself failed (`!rawIsMine`). That is B47's
   * own territory — a failed context read already holds, then lifts into
   * Today's retry surface once its own deadline passes, regardless of the
   * role fact (`today.onboardingFailClosed.b47.test.tsx`) — and this clause
   * must not re-trap a reader B47 already gives a way out. It fires only over
   * a row this account actually has in hand.
   *
   * Gated on `uid`: a signed-out reader has no row to be ambiguous about, and
   * already settles `failed` on purpose so the routes' own redirects run —
   * holding here would trap her behind a skeleton instead.
   */
  const contextAttributed = !contextLoading && rawIsMine;
  const athleteOnboarded =
    athleteRaw._onboarding_completed_at != null || athleteRaw._onboarding_completed === true;
  const bareRow = athleteRaw._account_role == null && !athleteOnboarded;
  const holdOnAmbiguousFailure = failed && !!uid && contextAttributed && bareRow;

  /**
   * ⚖️ B182 (decision Y, 2026-09-23) — ARM ON THE HOLD, CLEAR ON A RESOLVE.
   *
   * Runs on every mounted instance, but is idempotent at module level (see
   * `armAmbiguousHoldBackoff`/`clearAmbiguousHoldBackoff` above): several
   * instances observing the same ambiguous hold at once arm at most one pair
   * of timers for the account.
   *
   * ⚠️ CLEARS ONLY ON `resolved`, NOT ON EVERY `!holdOnAmbiguousFailure` —
   * deliberately narrower than the arm condition's mirror. A SECOND mounted
   * instance for an account already held (nav chrome mounting after the
   * guard, a route remount) starts its own local `state` at `'pending'`,
   * so on its first commit `holdOnAmbiguousFailure` reads false for THAT
   * instance alone, before its own cache-fed settle has landed — clearing
   * there would cancel the other instance's already-armed timers out from
   * under it, every time a second instance so much as mounts. A resolve is
   * unambiguous regardless of which instance observes it first (`settle`
   * never bails on a status change), so it is the one signal safe to key
   * the clear on. The cost, accepted: a hold that ends because the row
   * stopped being bare (rather than because the fetch resolved) leaves a
   * harmless spare background re-ask running — one wasted, idempotent
   * request, never a wrong render.
   */
  useEffect(() => {
    if (!uid) return;
    if (holdOnAmbiguousFailure) {
      armAmbiguousHoldBackoff(uid);
    } else if (resolved) {
      clearAmbiguousHoldBackoff(uid);
    }
  }, [uid, holdOnAmbiguousFailure, resolved]);

  return {
    loading,
    role,
    resolved,
    failed,
    // ⚖️ Server truth, else the pre-migration signal. NEVER a bare 'athlete'.
    //
    // ⚑ THE MIGRATION-WINDOW VETO. Lane A's endpoint is deliberately
    // "pre-migration safe": while the role-fact column does not exist yet it
    // answers a 200 `{ role: 'athlete' }` for EVERY account, coaches included
    // (B76A-LANE-REPORT §1). A 200 is authoritative here, so without this
    // clause every coach would be handed the athlete app for the whole window
    // between lane A shipping and the backfill landing — the exact leak the
    // ratified design forbids, and a visible regression on any device test
    // taken in that window.
    //
    // So a 'coach' answer from the server, or a positive coach identification
    // from the pre-migration signal, holds the boundary. This is NOT a vote
    // that dilutes the one fact: the legacy predicate is
    // `_account_role === 'coach' && !athleteOnboarded`, which is false for
    // every genuine athlete and false for every athlete_coach (they have
    // completed athlete onboarding by definition). It can therefore only fire
    // for the coach-first population it is protecting.
    //
    // 🗑️ REMOVE `|| legacyCoachOnly` once the backfill is confirmed in
    // production; after that a real coach's own role fact says 'coach' and
    // this clause is dead weight.
    coachOnly: resolved ? role === 'coach' || legacyCoachOnly : legacyCoachOnly,
    // Hold on auth + athlete context + the fetch. Athlete context is in here
    // because the fallback derives from it: releasing the skeleton before it
    // loads could show athlete UI to a coach whose fetch failed.
    //
    // B182 (decision Y) — and on a failed fetch over a row with no
    // `_account_role` marker, which never resolves the legacy fallback
    // either: see `holdOnAmbiguousFailure` above.
    boundaryPending: loading || contextLoading || holdOnAmbiguousFailure,
    hasAthleteSide: role === 'athlete' || role === 'athlete_coach',
    hasCoachSide: role === 'coach' || role === 'athlete_coach',
    refetch,
    ambiguousHold: holdOnAmbiguousFailure,
  };
}
